import { Button } from '@eventbrite/eds-button';
import { CardList } from '@eventbrite/eds-card-list';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import classNames from 'classnames';
import React, {
    FunctionComponent,
    MouseEventHandler,
    ReactNode,
    useState,
} from 'react';
import {
    CTA_JOIN_WAITLIST,
    PROMO_CODE_TYPES,
    TicketClassCategory,
} from '../../../constants';
import { shouldShowSoldOut } from '../../../containers/ticketSelection/utils/ticketUtils';
import {
    getTicketForImage,
    getTicketImageUrls,
    Ticket,
    TicketStatus,
    TicketVariant,
} from '../../../models/tickets';
import TicketImageThumbnail from '../../ticketImageThumbnail/TicketImageThumbnail';
import { TicketVariantForSelectionListProps } from '../constants';
import RemainingTickets from '../RemainingTickets';
import '../ticketCardContent.scss';
import TicketCardGraphicWrapper from '../TicketCardGraphicWrapper';
import TicketCardStatus, { TicketCardStatusProps } from '../TicketCardStatus';
import TicketContent, { TicketContentProps } from '../TicketContent';
import ToggleExpansionLink from '../ToggleExpansionLink';
import { getDonationChangeActionPayload } from '../utils';

export interface DisplayContentWithMultipleInputVariantsProps
    extends Pick<TicketContentProps, 'isFormSubmitting'> {
    /**
     * ID of the ticket to be passed up along with the selected value in the callback
     **/
    id: string;
    /**
     * Represents the original cost of the ticket prior to discounts applied
     **/
    displayPrice: GenericLazyString | string | null;
    /**
     * The array of string translations representing the associated fees and taxes. Should include the appropriate
     * copy. If not included, the section will not be rendered.
     **/
    feeAndTax?: Array<GenericLazyString>;
    /**
     * The currently selected quantity of the ticket. Should represent current state of the
     * select box.
     **/
    selectedQuantity?: number;
    /**
     * Whether ticket select dropdown should be disabled or not. Dropdown is enabled by default
     **/
    isDisabled?: boolean;
    /**
     * The name of the ticket to be displayed
     **/
    name: string;
    /**
     * Description for a the given ticket. If not included, no description will be rendered.
     **/
    description: ReactNode;
    /**
     * List of variants that will be rendered in the dropdown
     **/
    variants: Array<TicketVariantForSelectionListProps>;
    /**
     * A key tied to the ticketStatus map located in ticketStatusConstants.js. If nothing is passed in, then
     * no status will be displayed. Otherwise will render the associated status and also hide the
     * appropriate components as well.
     **/
    statusKey: TicketStatus;
    /**
     * The end datetime for ticket sales to be shown. Only required when certain statuses need
     * to be rendered.
     **/
    salesEnd: TicketCardStatusProps['salesEnd'];
    /**
     * The start datetime for ticket sales. Only required when rendering a status that needs the
     * date.
     **/
    salesStart: TicketCardStatusProps['salesStart'];
    /**
     * The number of tickets remaining for this specific ticket type. If passed in, it will be displayed.
     **/
    quantityRemaining?: number;
    /**
     * Callback for when the selected value changes
     **/
    onQuantityChange: (payload: {
        variantId?: string;
        id: string;
        quantity: number;
    }) => void;
    /**
     * Callback when join waitlist button clicked
     */
    onJoinWaitlist: MouseEventHandler<HTMLButtonElement>;
    /**
     * This is a necessary flag for cases where the tickets may have a status causing elements to appear
     * or disappear but the status itself should not be displayed. An example of this is event-wide
     * sales not started or sales ended. Each ticket should not display it individually, but no select
     * should appear.
     **/
    shouldHideStatus: boolean;
    onToggle?: (isExpanded: boolean) => void;
    initiallyExpanded: boolean;
    maximumQuantityPerOrder?: number | null;
    donationAmount: number | null;
    onDonationChange?: (payload: {
        variantId?: string;
        id: string;
        amount: any;
    }) => void;
    currencyFormat: string;
    image?: Ticket['image'];
    hideSaleDates: boolean;
    hasPrimaryVariant: boolean;
    priceRange: string;
    showDiscountedPill: boolean;
    showJoinWaitlist: boolean;
    showUnlockedPill: boolean;
}

const _calculateMaxQuantityAvailable = (
    maximumQuantityPerOrder: number,
    totalTicketsSelected: number,
    selectedQuantity: number,
) => maximumQuantityPerOrder - totalTicketsSelected + selectedQuantity;

const getTicketOptionsRange = ({
    variant,
    maximumQuantityPerTicketGroup,
    totalTicketsSelected,
}: {
    variant: DisplayContentWithMultipleInputVariantsProps['variants'][number];
    maximumQuantityPerTicketGroup: DisplayContentWithMultipleInputVariantsProps['maximumQuantityPerOrder'];
    totalTicketsSelected: number;
}) => {
    // Admission variants range need to be computed. Addon variants do not.
    if (variant.category !== TicketClassCategory.admission) {
        return variant.ticketOptionsRange;
    }

    /**
     *  Rule 1
     *     If the variant level's variantMaxQuantity is not null
     *     then variants must respect that
     *  Rule 2
     *    If the ticket level's maximumQuantityPerTicketGroup is not null,
     *    then the sum of quantity for the variants for the ticket must
     *    also respect that in addition to variant level's variantMaxQuantity
     *  Rule 3
     *    If the ticket level's maximumQuantityPerTicketGroup is null, then we only care
     *    about the variants variantMaxQuantity
     */
    const validRangeOptionMax = maximumQuantityPerTicketGroup
        ? _calculateMaxQuantityAvailable(
              maximumQuantityPerTicketGroup,
              totalTicketsSelected,
              variant.selectedQuantity,
          )
        : // We should get the largest number from the ticket options range in order to get the max quantity
          Math.max(...variant.ticketOptionsRange);

    return variant.ticketOptionsRange.filter(
        (ticketOption) => ticketOption <= validRangeOptionMax,
    );
};

export const DisplayContentWithMultipleInputVariants: FunctionComponent<
    DisplayContentWithMultipleInputVariantsProps
> = ({
    currencyFormat,
    description,
    displayPrice,
    hasPrimaryVariant,
    hideSaleDates,
    id,
    image,
    initiallyExpanded,
    isFormSubmitting,
    maximumQuantityPerOrder,
    name,
    onDonationChange,
    onJoinWaitlist,
    onQuantityChange,
    onToggle,
    priceRange,
    quantityRemaining,
    salesEnd,
    salesStart,
    shouldHideStatus,
    showDiscountedPill,
    showJoinWaitlist,
    showUnlockedPill,
    statusKey,
    variants,
}) => {
    const [isExpanded, setIsExpanded] = useState(initiallyExpanded);

    const _handleQuantityChange = (
        variantId: string | null,
        quantity: string,
    ) => {
        const basePayload = {
            id,
            quantity: parseInt(quantity, 10),
        };

        const payload = variantId
            ? {
                  ...basePayload,
                  variantId,
              }
            : basePayload;

        onQuantityChange(payload);
    };

    const _handleDonationChange = (
        variantId: string | undefined,
        amount: string,
    ) => {
        onDonationChange?.(
            getDonationChangeActionPayload(
                id,
                amount,
                currencyFormat,
                variantId,
            ),
        );
    };

    const _handleToggleExpansion = () => {
        const newIsExpandedValue = !isExpanded;
        setIsExpanded(newIsExpandedValue);
        onToggle?.(newIsExpandedValue);
    };

    const _getMaximumQuantityPerOrder = () => {
        const { maximumQuantityPerOrder, maxQuantity } = variants.find(
            (e) => !!e,
        ) ?? { maximumQuantityPerOrder: null, maxQuantity: null };

        return maximumQuantityPerOrder || maxQuantity || 0;
    };

    // All admission variants share maximum quantity per order - this should be edited
    _getMaximumQuantityPerOrder();
    const totalTicketsSelected = variants.reduce(
        (acc, singleVariant) => acc + singleVariant.selectedQuantity,
        0,
    );

    const items = variants.map((variant) => {
        const ticketId = variant.primary ? id : variant.id;

        const ticketOptionsRange = getTicketOptionsRange({
            variant,
            maximumQuantityPerTicketGroup: maximumQuantityPerOrder,
            totalTicketsSelected,
        });

        // Specific to public discounted items.
        // For public discounted items, we want to:
        // - show the display price (public discounted price) as well as the full price.
        const primaryPrice =
            variant.discountType === PROMO_CODE_TYPES.PUBLIC
                ? displayPrice
                : null;

        const ticketForImage = getTicketForImage<
            Pick<TicketVariant, 'image'> & {
                name: GenericLazyString | string;
            },
            Pick<
                DisplayContentWithMultipleInputVariantsProps,
                'image' | 'name' | 'variants'
            >,
            Pick<TicketVariant, 'image'> & {
                name: GenericLazyString | string;
            }
        >(
            {
                name,
                image,
                variants,
            },
            variant,
        );
        const ticketGraphic = ticketForImage ? (
            <TicketImageThumbnail
                title={ticketForImage.name}
                {...getTicketImageUrls(ticketForImage)}
            />
        ) : null;

        return {
            id: ticketId,
            content: (
                <TicketContent
                    category={variant.category}
                    currencyFormat={null}
                    currencySymbol={null}
                    description={variant.description}
                    displayPrice={variant.displayPrice ?? null}
                    donationAmount={variant.donationAmount}
                    feeAndTax={variant.feeAndTax}
                    graphic={ticketGraphic}
                    hideSaleDates={variant.hideSaleDates}
                    hideSelect={variant.hideSelect}
                    id={ticketId}
                    includeFee={variant.includeFee}
                    includeTax={variant.includeTax}
                    isBOGOTicket={false}
                    isCurrencySuffix={false}
                    isFormSubmitting={isFormSubmitting}
                    isTicketTiered={variant.isTicketTiered}
                    name={variant.name}
                    onDonationChange={(newValue) =>
                        _handleDonationChange(ticketId, newValue)
                    }
                    onJoinWaitlist={null}
                    onQuantityChange={_handleQuantityChange}
                    paymentConstraints={variant.paymentConstraints}
                    primaryPrice={primaryPrice ?? variant.primaryPrice ?? null}
                    quantityRemaining={variant.quantityRemaining}
                    salesEnd={variant.salesEnd}
                    salesStart={variant.salesStart}
                    selectedQuantity={variant.selectedQuantity}
                    shouldHideStatus={hasPrimaryVariant}
                    showDiscountedPill={showDiscountedPill}
                    showJoinWaitlist={variant.showJoinWaitlist}
                    showUnlockedPill={showUnlockedPill}
                    statusKey={variant.statusKey}
                    ticketOptionsRange={ticketOptionsRange}
                    ticketReseller={variant.ticketReseller}
                    urgencySignals={null}
                    variantId={variant.id}
                />
            ),
        };
    });

    if (description) {
        items.push({
            id: 'ticketDescription',
            content: <div className="eds-l-mar-all-4">{description}</div>,
        });
    }

    const containerClassNames = classNames(
        'eds-g-cell eds-g-cell-12-12 eds-l-pad-vert-4',
        'ticket-border',
    );

    const primaryDisplayClassNames = classNames(
        'eds-g-cell eds-g-cell-12-12 eds-ticket-card-content eds-ticket-display-card-content eds-l-pad-all-0',
        {
            'eds-ticket-display-card-content--waitlist': showJoinWaitlist,
            'eds-ticket-card-content--is-disabled': shouldShowSoldOut({
                showJoinWaitlist,
                statusKey,
            }),
            'eds-ticket-display-card-content--multiple-variants':
                !shouldShowSoldOut({
                    showJoinWaitlist,
                    statusKey,
                }),
        },
    );

    const expandedContent = isExpanded ? (
        <div
            className="ticket-variants-list"
            data-spec="ticket-variants-list-wrapper"
            data-automation="ticket-variants-list-wrapper"
            data-testid="ticket-variants-list-wrapper"
        >
            <div data-automation="ticket-variant-list-items">
                <CardList items={items} />
            </div>
        </div>
    ) : null;
    const ticketForImage = getTicketForImage<
        Pick<TicketVariant, 'image'> & { name: GenericLazyString | string },
        Pick<
            DisplayContentWithMultipleInputVariantsProps,
            'image' | 'name' | 'variants'
        >,
        Pick<TicketVariant, 'image'> & { name: GenericLazyString | string }
    >({
        image,
        variants,
        name,
    });
    const ticketGrahicWrapper = ticketForImage ? (
        <TicketCardGraphicWrapper
            graphic={
                <TicketImageThumbnail
                    title={ticketForImage.name}
                    {...getTicketImageUrls(ticketForImage)}
                />
            }
        />
    ) : null;

    const soldOutLabel = shouldShowSoldOut({
        showJoinWaitlist,
        statusKey,
    }) ? (
        <div className="ticket-sold-out eds-text-bm eds-text-height--open eds-text-color--ui-500 eds-l-pad-left-2">
            {gettext('Sold Out')}
        </div>
    ) : null;

    const joinWaitlist = showJoinWaitlist ? (
        <div
            className="eds-ticket-card-content__join-waitlist-button"
            data-spec="join-waitlist-button"
            data-automation={`join-waitlist-ticket-${id}-btn`}
        >
            <Button onClick={onJoinWaitlist}>{CTA_JOIN_WAITLIST}</Button>
        </div>
    ) : null;

    const quantityRemainingLabel = quantityRemaining ? (
        <RemainingTickets quantityRemaining={quantityRemaining} />
    ) : null;

    const hideStatus = shouldHideStatus || hideSaleDates;

    return (
        <div
            className={containerClassNames}
            data-spec="content-with-multiple-variants-container"
            data-testid="content-with-multiple-variants-container"
        >
            <div
                className={primaryDisplayClassNames}
                data-automation="ticket-card-primary-content-wrapper"
                data-testid="ticket-card-primary-content-wrapper"
            >
                {ticketGrahicWrapper}
                <div className="eds-ticket-card-content__primary-content eds-g-cell eds-g-cell-12-12">
                    <h3 className="ticket-title">{name}</h3>
                    <ToggleExpansionLink
                        text={priceRange}
                        altText={priceRange}
                        isExpanded={isExpanded}
                        onToggle={_handleToggleExpansion}
                        trailingText={null}
                    />
                </div>
                {soldOutLabel}
                {joinWaitlist}
            </div>
            {expandedContent}
            {quantityRemainingLabel}
            <TicketCardStatus
                shouldHideStatus={hideStatus}
                statusKey={statusKey}
                salesStart={salesStart}
                salesEnd={salesEnd}
                isTicketTiered={false}
            />
        </div>
    );
};

export default DisplayContentWithMultipleInputVariants;
